/**
 * Admin → Settings → Jev — feature E07-F12 (PROTOTYPE, stack-native).
 *
 * The Jev block of the AI settings, next to Model Matrix (spec
 * docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.7). Four
 * sections behind a left rail (one screen each):
 *
 *   jev-connection     — OpenRouter key (masked), Jev on/off, «AI-check verdicts:
 *                        Jev / Haiku» switch, Save with plain-text errors (T01);
 *   jev-question-sets  — sets list + question editor (JevQuestionSets.tsx, T02);
 *   jev-thresholds     — thresholds, history, rollback, freeze, safeguard
 *                        warnings, recalibration (JevThresholds.tsx, T03);
 *   jev-metrics        — 30-day metrics + «Check set on a row» (JevMetrics.tsx, T04).
 *
 * Admin-only: SettingsView renders this tab for role admin only; the endpoints
 * sit behind requireRole('admin') too. Transport: jevApi.ts (mock at this stage).
 *
 * COPY: hardcoded English, the codebase convention (no i18n runtime; Russian copy
 * leaking into the UI was a P0 in the 2026-07 fidelity audit).
 */
import { useEffect, useState } from 'react';
import { SegmentedToggle, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useJevQuestionSets,
  useJevSettings,
  useJevThresholds,
  useUpdateJevSettings,
  type JevKeyAction,
  type JevSettings,
  type JevVerdictPath,
} from './jevApi.js';
import { formatDateTime, isJevReady } from './jevModel.js';
import { BTN_PRIMARY, Card, FIELD_CLASS, Loading, Notice, Pill, SectionHeading } from './jevUi.js';
import { JevQuestionSets } from './JevQuestionSets.js';
import { JevThresholds } from './JevThresholds.js';
import { JevMetrics } from './JevMetrics.js';

type JevSection = 'connection' | 'sets' | 'thresholds' | 'metrics';

const SECTIONS: readonly { id: JevSection; label: string; icon: string; testId: string }[] = [
  { id: 'connection', label: 'Connection', icon: 'fa-plug', testId: 'jev-nav-connection' },
  { id: 'sets', label: 'Question sets', icon: 'fa-list-ol', testId: 'jev-nav-sets' },
  { id: 'thresholds', label: 'Thresholds', icon: 'fa-sliders', testId: 'jev-nav-thresholds' },
  { id: 'metrics', label: 'Metrics & check', icon: 'fa-chart-column', testId: 'jev-nav-metrics' },
];

// ---------------------------------------------------------------------------
// T01 — Connection
// ---------------------------------------------------------------------------

interface ConnectionForm {
  readonly keyInput: string;
  readonly keyCleared: boolean;
  readonly enabled: boolean;
  readonly verdictPath: JevVerdictPath;
}

function formFrom(s: JevSettings): ConnectionForm {
  return { keyInput: '', keyCleared: false, enabled: s.enabled, verdictPath: s.verdictPath };
}

function keyAction(f: ConnectionForm): JevKeyAction {
  if (f.keyInput.trim()) return { kind: 'set', value: f.keyInput.trim() };
  if (f.keyCleared) return { kind: 'clear' };
  return { kind: 'keep' };
}

function JevConnection({ settings }: { readonly settings: JevSettings }): React.JSX.Element {
  const update = useUpdateJevSettings();
  const toast = useToast();
  const [form, setForm] = useState<ConnectionForm>(() => formFrom(settings));
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => setForm(formFrom(settings)), [settings]);

  const dirty =
    form.keyInput.trim() !== '' ||
    form.keyCleared ||
    form.enabled !== settings.enabled ||
    form.verdictPath !== settings.verdictPath;

  function patch(change: Partial<ConnectionForm>): void {
    setSaveError(null);
    setForm((f) => ({ ...f, ...change }));
  }

  function save(): void {
    setSaveError(null);
    update.mutate(
      { key: keyAction(form), enabled: form.enabled, verdictPath: form.verdictPath },
      {
        onSuccess: () => toast.success('Jev settings saved'),
        onError: (err) => {
          const msg = errorMessage(err, 'Could not save Jev settings');
          setSaveError(msg);
          toast.error(msg);
        },
      },
    );
  }

  const keyPlaceholder = form.keyCleared
    ? 'key will be removed on save'
    : settings.hasKey && settings.maskedKey
      ? `saved: ${settings.maskedKey}`
      : 'no key set — paste an OpenRouter key (sk-or-…)';

  return (
    <Card testId="jev-connection">
      <SectionHeading
        title="Connection"
        hint="Jev answers questions with probabilities instead of text. It uses its own OpenRouter key — separate from the Anthropic / OpenAI keys in Model Matrix — so the key can be replaced here without a deploy."
        right={
          <Pill tone={isJevReady(settings) ? 'success' : 'neutral'} testId="jev-status-pill">
            <i
              className={`fa-solid ${isJevReady(settings) ? 'fa-circle-check' : 'fa-circle-pause'}`}
              aria-hidden
            />
            {isJevReady(settings) ? 'Jev is on' : 'Jev is not configured'}
          </Pill>
        }
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <label htmlFor="jev-key" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">OpenRouter key</span>
          <input
            id="jev-key"
            data-testid="jev-key"
            type="password"
            autoComplete="off"
            spellCheck={false}
            className={`${FIELD_CLASS} font-mono`}
            value={form.keyInput}
            placeholder={keyPlaceholder}
            onChange={(e) => patch({ keyInput: e.target.value, keyCleared: false })}
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            <span data-testid="jev-key-note" className="text-[11px] text-gray-500">
              {form.keyCleared
                ? 'The key will be removed on save.'
                : form.keyInput
                  ? 'The new key replaces the saved one on save.'
                  : settings.hasKey
                    ? 'Leave empty to keep the saved key. The full key is never shown again.'
                    : 'No key — every Jev button stays disabled with “Jev is not configured”.'}
            </span>
            {settings.hasKey && !form.keyCleared ? (
              <button
                type="button"
                data-testid="jev-key-clear"
                onClick={() => patch({ keyInput: '', keyCleared: true })}
                className="shrink-0 text-[11px] font-medium text-red-600 hover:underline"
              >
                Remove key
              </button>
            ) : null}
          </div>
        </label>

        <div className="grid gap-4">
          <div>
            <span className="mb-1 block text-xs font-medium text-text-strong">Jev</span>
            <label className="inline-flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                role="switch"
                data-testid="jev-enabled"
                className="peer sr-only"
                checked={form.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              <span
                aria-hidden
                className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-[var(--color-primary)] peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent)]"
              />
              <span className="text-sm text-text-strong">{form.enabled ? 'On' : 'Off'}</span>
            </label>
            <p className="mt-1 text-[11px] text-gray-500">
              Off hides Jev-triage and makes the Jev buttons inactive. Nothing switches on or off by
              itself.
            </p>
          </div>

          <div>
            <span className="mb-1 block text-xs font-medium text-text-strong">
              AI-check verdicts
            </span>
            <SegmentedToggle<JevVerdictPath>
              ariaLabel="AI-check verdicts"
              testId="jev-verdict-path"
              value={form.verdictPath}
              onChange={(v) => patch({ verdictPath: v })}
              options={[
                { value: 'jev', label: 'Jev' },
                { value: 'haiku', label: 'Haiku (rollback)' },
              ]}
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Applies to new checks only — rows already checked keep the path they were checked by.
              There is no automatic switch on errors: if Jev is unavailable, the row fails with “Jev
              unavailable”.
            </p>
          </div>
        </div>
      </div>

      {saveError ? (
        <div className="mt-4">
          <Notice tone="danger" testId="jev-save-error">
            {saveError}
          </Notice>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <span className="text-[11px] text-text-muted" data-testid="jev-connection-meta">
          Last saved {formatDateTime(settings.updatedAt)} by {settings.updatedBy}
        </span>
        <button
          type="button"
          data-testid="jev-connection-save"
          disabled={!dirty || update.isPending}
          onClick={save}
          className={BTN_PRIMARY}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Screen root
// ---------------------------------------------------------------------------

export function JevTab(): React.JSX.Element {
  const settingsQuery = useJevSettings();
  const setsQuery = useJevQuestionSets();
  const thresholdsQuery = useJevThresholds();
  const [section, setSection] = useState<JevSection>('connection');

  const warningCount = (thresholdsQuery.data ?? []).reduce((n, t) => n + t.warnings.length, 0);
  const uncalibrated = (setsQuery.data ?? []).filter((s) => !s.calibrated).length;

  function railMeta(id: JevSection): React.ReactNode {
    if (id === 'connection' && settingsQuery.data) {
      return isJevReady(settingsQuery.data) ? (
        <Pill tone="success">on</Pill>
      ) : (
        <Pill tone="neutral">off</Pill>
      );
    }
    if (id === 'sets' && setsQuery.data) {
      return uncalibrated > 0 ? (
        <Pill tone="warning">{uncalibrated} not calibrated</Pill>
      ) : (
        <Pill>{setsQuery.data.length}</Pill>
      );
    }
    if (id === 'thresholds' && warningCount > 0) {
      return (
        <Pill tone="warning">
          {warningCount} warning{warningCount === 1 ? '' : 's'}
        </Pill>
      );
    }
    return null;
  }

  return (
    <div data-testid="jev-tab" className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-brand text-2xl font-semibold text-text-strong">Jev</h2>
          <p className="mt-1 max-w-3xl text-sm text-text-muted">
            The decision model for Company Research: long-list triage, AI-check verdicts and
            duplicate hints. Every paid action here runs only from a button, after a confirmation
            that shows the price.
          </p>
        </div>
        <Pill tone="primary">
          <i className="fa-solid fa-lock text-[10px]" aria-hidden /> Admin only
        </Pill>
      </header>

      {settingsQuery.data && !isJevReady(settingsQuery.data) ? (
        <Notice tone="warning" testId="jev-not-configured">
          <strong>Jev is not configured.</strong> Without a key, or with Jev switched off,
          Jev-triage is hidden, the set check and recalibration are disabled, and AI-check must use
          Haiku.
        </Notice>
      ) : null}

      <div className="grid gap-6 md:grid-cols-[13rem_minmax(0,1fr)]">
        <nav
          aria-label="Jev sections"
          className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible"
        >
          {SECTIONS.map((s) => {
            const active = s.id === section;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={s.testId}
                aria-current={active ? 'page' : undefined}
                onClick={() => setSection(s.id)}
                className={[
                  'flex shrink-0 items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors md:border-l-4',
                  active
                    ? 'bg-white font-semibold text-primary shadow-sm md:border-[var(--color-accent)]'
                    : 'text-text-muted hover:bg-white/60 hover:text-text-strong md:border-transparent',
                ].join(' ')}
              >
                <i className={`fa-solid ${s.icon} w-4 text-xs`} aria-hidden />
                <span className="flex-1 whitespace-nowrap">{s.label}</span>
                {railMeta(s.id)}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0">
          {settingsQuery.isLoading ? <Loading testId="jev-loading" /> : null}
          {settingsQuery.isError ? (
            <Notice tone="danger" testId="jev-load-error">
              {errorMessage(settingsQuery.error, 'Failed to load Jev settings')}
            </Notice>
          ) : null}
          {settingsQuery.data ? (
            <>
              {section === 'connection' ? <JevConnection settings={settingsQuery.data} /> : null}
              {section === 'sets' ? <JevQuestionSets /> : null}
              {section === 'thresholds' ? (
                <JevThresholds jevReady={isJevReady(settingsQuery.data)} />
              ) : null}
              {section === 'metrics' ? (
                <JevMetrics jevReady={isJevReady(settingsQuery.data)} />
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default JevTab;
